import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import { Link } from 'react-router-dom';

const Dashboard = ({ isAdmin, apiEndpoints }) => {
  const [apiVersion, setApiVersion] = useState('v1');
  const [responseData, setResponseData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalCodes: 0,
    groupCount: 0,
    validCodes: 0,
    apiStatus: 'Online'
  });
  const [loading, setLoading] = useState(true);
  
  // Fetch system stats on component mount
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Get total codes count
        const codesResponse = await fetch('/codes?page=1&per_page=1');
        const totalCodes = codesResponse.headers.get('X-Total-Count') || 0;
        
        // Get groups count
        const groupsResponse = await fetch('/groups');
        const groupsData = await groupsResponse.json();
        
        // Get valid codes count
        const validCodesResponse = await fetch('/codes?valid_only=true&page=1&per_page=1');
        const validCodes = validCodesResponse.headers.get('X-Total-Count') || 0;
        
        // Get a few sample codes
        const sampleCodesResponse = await fetch('/codes?term=F32&page=1&per_page=3');
        const sampleCodes = await sampleCodesResponse.json();
        setResponseData(sampleCodes);
        
        // Update dashboard stats
        setDashboardStats({
          totalCodes: parseInt(totalCodes),
          groupCount: groupsData.length || 0,
          validCodes: parseInt(validCodes),
          apiStatus: 'Online'
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setDashboardStats(prev => ({
          ...prev,
          apiStatus: 'Error'
        }));
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  return (
    <div className="dashboard">
      {localStorage.getItem('justLoggedIn') === 'true' && (
        <div className="alert alert-success" style={{ 
          borderRadius: '8px', 
          padding: '16px',
          backgroundColor: '#d4edda',
          textAlign: 'center'
        }}>
          Successfully authenticated from Microsoft account.
          {(() => {
            localStorage.removeItem('justLoggedIn');
            return null;
          })()}
        </div>
      )}
      
      <div style={{ maxWidth: '900px', margin: '0 auto', marginTop: '20px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="card-title mb-0">Welcome to Kipu ICD Service</h1>
          <div className="d-flex align-items-center">
            <div className="badge rounded-pill bg-light text-dark me-3 px-3 py-2">
              <span className="text-muted me-2">API:</span> 
              <span style={{ fontWeight: 'bold' }}>{apiVersion}</span>
            </div>
            <div className={`badge rounded-pill px-3 py-2 ${dashboardStats.apiStatus === 'Online' ? 'bg-success' : 'bg-danger'}`}>
              <i className={`fas fa-${dashboardStats.apiStatus === 'Online' ? 'signal' : 'exclamation-triangle'} me-2`}></i>
              {dashboardStats.apiStatus}
            </div>
          </div>
        </div>
        
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '30px' }}>
          This service provides ICD-10 and DSM-5 diagnostic codes for the EMR system. 
          ICD-10 codes are updated manually from cms.gov downloads, while DSM-5 codes are maintained through separate data imports. 
          This service enables searching for diagnostic codes by code number (e.g., "A00") or by search terms (e.g., "fever").
        </p>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem' }}></i>
          <p>Loading dashboard metrics...</p>
        </div>
      ) : (
        <div className="metrics-dashboard" style={{ 
          maxWidth: '1000px', 
          margin: '0 auto',
          marginTop: '20px'
        }}>
          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px' }}>
            {/* Total Codes */}
            <div className="metric-card" style={{
              flex: '1 1 300px',
              maxWidth: '320px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              padding: '20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '24px', color: '#3498db', marginBottom: '10px' }}>
                <i className="fas fa-database" style={{ marginRight: '10px' }}></i>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#555', margin: '0 0 10px 0' }}>TOTAL CODES</h3>
              <p style={{ fontSize: '28px', fontWeight: '600', color: '#333', margin: '0' }}>
                {dashboardStats.totalCodes.toLocaleString()}
              </p>
            </div>

            {/* Valid Codes */}
            <div className="metric-card" style={{
              flex: '1 1 300px',
              maxWidth: '320px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              padding: '20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '24px', color: '#2ecc71', marginBottom: '10px' }}>
                <i className="fas fa-check-circle" style={{ marginRight: '10px' }}></i>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#555', margin: '0 0 10px 0' }}>VALID CODES</h3>
              <p style={{ fontSize: '28px', fontWeight: '600', color: '#333', margin: '0' }}>
                {dashboardStats.validCodes.toLocaleString()}
              </p>
              <p style={{ fontSize: '14px', color: '#888', marginTop: '5px' }}>
                ({Math.round((dashboardStats.validCodes / dashboardStats.totalCodes) * 100)}% of total)
              </p>
            </div>

            {/* Groups Count */}
            <div className="metric-card" style={{
              flex: '1 1 300px',
              maxWidth: '320px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              padding: '20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '24px', color: '#9b59b6', marginBottom: '10px' }}>
                <i className="fas fa-layer-group" style={{ marginRight: '10px' }}></i>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#555', margin: '0 0 10px 0' }}>ICD CATEGORIES</h3>
              <p style={{ fontSize: '28px', fontWeight: '600', color: '#333', margin: '0' }}>
                {dashboardStats.groupCount}
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="admin-section mt-5 text-center">
              <div className="admin-banner" style={{
                maxWidth: '600px',
                margin: '0 auto',
                padding: '25px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                position: 'relative'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: '-15px', 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  padding: '5px 15px',
                  borderRadius: '20px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  ADMIN ACCESS
                </div>
                <h4 style={{ fontSize: '18px', color: '#444', marginBottom: '15px' }}>
                  <i className="fas fa-user-shield me-2" style={{ color: '#e74c3c' }}></i>
                  You have administrative privileges
                </h4>
                <p style={{ fontSize: '14px', color: '#666', maxWidth: '400px', margin: '0 auto 20px' }}>
                  Access the admin dashboard to manage users, update ICD codes, and configure system settings.
                </p>
                <Link 
                  to="/admin" 
                  className="btn btn-danger"
                  style={{ 
                    padding: '8px 20px',
                    borderRadius: '30px',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 4px 6px rgba(234, 76, 60, 0.2)'
                  }}
                >
                  <i className="fas fa-cog me-2"></i>
                  Open Admin Dashboard
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
      
      {responseData && (
        <div className="code-block" style={{ 
          textAlign: 'left', 
          maxWidth: '800px', 
          margin: '40px auto',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#1e2128', 
            color: '#abb2bf',
            fontSize: '14px',
            fontFamily: 'monospace',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>GET /codes?term=F32&limit=3</span>
            <span style={{ fontSize: '12px', backgroundColor: '#2ecc71', color: 'white', padding: '3px 6px', borderRadius: '4px' }}>
              200 OK
            </span>
          </div>
          <pre style={{ 
            margin: 0, 
            padding: '16px', 
            backgroundColor: '#282c34', 
            color: '#abb2bf',
            fontSize: '13px',
            fontFamily: 'monospace',
            overflow: 'auto',
            maxHeight: '400px' 
          }}>
{JSON.stringify(responseData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 
